import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import BadgeIcon from "@mui/icons-material/Badge";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import request from "../../../Helpers/API";
import Swal from "sweetalert2";
import { LoaderScreen } from "../../components/LoaderScreen";
import { useBox } from "../../Finanzas/Helper/useBox";
import { VisitasMetricas } from "./visitasprogramadas/VisitasMetricas";
import { VisitasListado } from "./visitasprogramadas/VisitasListado";
import { VisitasCalendario } from "./visitasprogramadas/VisitasCalendario";
import { VisitasModalNueva } from "./visitasprogramadas/VisitasModalNueva";
import { VisitasModalDetalle } from "./visitasprogramadas/VisitasModalDetalle";
import { VisitasModalEstatus } from "./visitasprogramadas/VisitasModalEstatus";
import { VisitasModalComentario } from "./visitasprogramadas/VisitasModalComentario";
import { VisitasModalRevertir } from "./visitasprogramadas/VisitasModalRevertir";
import { FiltroVisitasProgramadas } from "./visitasprogramadas/FiltroVisitasProgramadas";
import { MESES, ESTATUS_VISITA, estiloEstatus, construirCalendario, construirSemana } from "./visitasprogramadas/datosVisitas";
import GetAppIcon from "@mui/icons-material/GetApp";
import { GetKey } from "../../../Helpers/GetKey";


const HOY = new Date();
const ROL_VESC = "VESC";
// Rol con permiso para revertir una visita ya realizada de vuelta a pendiente
const ROL_URVR = "URVR";
const PRIMER_DIA_MES = new Date(HOY.getFullYear(), HOY.getMonth(), 1).toISOString().substring(0, 10);
const ULTIMO_DIA_MES = new Date(HOY.getFullYear(), HOY.getMonth() + 1, 0).toISOString().substring(0, 10);

export const CRM_Visitas_Screen = () => {
  const history = useHistory();
  const location = useLocation();
  const { usuario } = useSelector((store) => store.usuario);
  const { roles } = useSelector((store) => store.sesion);
  // Rol VESC (vendedor) solo ve sus propias visitas — se oculta la columna Asesor
  const esRolVesc = roles.some((rol) => rol.idrol === ROL_VESC);
  // Rol URVR — puede revertir una visita ya realizada de vuelta a pendiente
  const esRolUrvr = roles.some((rol) => rol.idrol === ROL_URVR);

  // Si se llega desde CRM_Dashboard con un cliente ya buscado (route state), la pantalla
  // queda fija a ese cliente: sin buscador libre y "Nueva visita" solo para él
  const idclienteFijo = location.state?.idcliente || null;
  const nombreClienteFijo = location.state?.cliente || "";

  const [ejercicio, setEjercicio] = useState(HOY.getFullYear());
  const [periodo, setPeriodo] = useState(HOY.getMonth() + 1); // 1-12

  const [boxstate, setBoxState] = useBox({
    fechaInicio: PRIMER_DIA_MES,
    fechaFin: ULTIMO_DIA_MES,
    pv: { adr: [], sucursal: [] },
  });
  const [permisos, setPermisos] = useState();

  const [listado, setListado] = useState([]);
  const [objetivo, setObjetivo] = useState(0);
  const [opcionesTipoVisita, setOpcionesTipoVisita] = useState([]);
  const [opcionesLineas, setOpcionesLineas] = useState([]);
  const [loader, setLoader] = useState({ mostrar: false, alerta: false, error: false, mensaje: "" });

  // Buscador — sugiere clientes, pero solo filtra la tabla al seleccionar uno
  const [clientes, setClientes] = useState([]);
  const [textoBusqueda, setTextoBusqueda] = useState("");
  const [mostrarDropdown, setMostrarDropdown] = useState(false);
  const [clienteFiltro, setClienteFiltro] = useState(
    idclienteFijo ? { idcliente: idclienteFijo, razon_social: nombreClienteFijo } : null
  );
  const searchRef = useRef(null);

  const [vista, setVista] = useState("listado"); // "listado" | "calendario"
  const [modoCalendario, setModoCalendario] = useState("mes"); // "mes" | "semana"
  const [filtroEstatusCalendario, setFiltroEstatusCalendario] = useState(""); // "" | "P" | "R" | "V"
  // Fecha real (no relativa a "hoy") que ancla la semana visible — se sincroniza con
  // ejercicio/periodo cuando la navegación semanal cruza a otro mes
  const [semanaAncla, setSemanaAncla] = useState(HOY);

  const [modalNuevaAbierto, setModalNuevaAbierto] = useState(false);
  const [visitaEditar, setVisitaEditar] = useState(null);
  const [idVisitaDetalle, setIdVisitaDetalle] = useState(null);
  const [visitaDetalle, setVisitaDetalle] = useState(null);
  const [timelineDetalle, setTimelineDetalle] = useState([]);
  const [visitaEstatus, setVisitaEstatus] = useState(null);
  const [visitaComentario, setVisitaComentario] = useState(null);
  const [visitaRevertir, setVisitaRevertir] = useState(null);

  // Listado de clientes del CRM — mismo endpoint que CRM_DetalleScreen / CRM_Dashboard
  useEffect(() => {
    request
      .get("Clientes/GetListadoClientesCRM")
      .then((result) => {
        const lista = result || [];
        setClientes(lista);
        // Completa el cliente fijo (venido por route state) con sus datos reales (rfc, etc.)
        if (idclienteFijo) {
          const completo = lista.find((c) => Number(c.idcliente) === Number(idclienteFijo));
          if (completo) setClienteFiltro(completo);
        }
      })
      .catch((error) => Swal.fire("VISITAS", JSON.stringify(error), "error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // adr/sucursal viajan como listas de ids separadas por coma (mismo patrón que
  // CRM_CotizacionesServicio); fechaInicio/fechaFin llegan del filtro (boxstate), por defecto
  // el mes actual completo. TODO: una vez actualizado ListadoVisitasProgramadas en backend para
  // aceptar estos parámetros, quitar ejercicio/periodo si dejan de ser necesarios.
  const cargarListado = (filtro) => {
    const f = filtro || { fechaInicio: boxstate.fechaInicio, fechaFin: boxstate.fechaFin, tipoFecha: "FV" };
    const adr = (boxstate.pv?.adr || []).map((a) => a.idadr).join(",") || "0";
    const sucursal = (boxstate.pv?.sucursal || []).map((s) => s.idsucursal).join(",") || "0";
    const tipoFecha = f.tipoFecha || "FV";

    setLoader((prev) => ({ ...prev, mostrar: true }));
    return request
      .get(`VisitasCRM/ListadoVisitasProgramadas?ejercicio=${ejercicio}&periodo=${periodo}&vendedor=0&fechainicio=${f.fechaInicio || PRIMER_DIA_MES}&fechafin=${f.fechaFin || ULTIMO_DIA_MES}&adr=${adr}&sucursal=${sucursal}&tipo_filtrado=${tipoFecha}`)
      .then((result) => {
        setListado(result?.listado_visitas || []);
        setObjetivo(result?.header_info?.objetivo || 0);
        setOpcionesTipoVisita(result?.opciones_tipo_visita || []);
        setOpcionesLineas(result?.opciones_lineas || []);
        setPermisos(result?.permisos);
        setLoader((prev) => ({ ...prev, mostrar: false }));
      })
      .catch((error) => {
        setLoader((prev) => ({ ...prev, mostrar: false }));
        Swal.fire("VISITAS", JSON.stringify(error), "error");
      });
  };

  useEffect(() => {
    cargarListado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ejercicio, periodo]);

  const filas = useMemo(() => {
    return listado
      .filter((v) => !clienteFiltro || Number(v.idcliente) === Number(clienteFiltro.idcliente))
      .map((v) => ({
        ...v,
        fechaObj: new Date(v.fecha_visita),
        estatusEstilo: estiloEstatus(v.estatus),
      }));
  }, [listado, clienteFiltro]);

  // Filtro de estatus exclusivo del calendario — no afecta métricas ni el listado
  const filasCalendario = useMemo(() => {
    return filtroEstatusCalendario
      ? filas.filter((v) => v.estatus === filtroEstatusCalendario)
      : filas;
  }, [filas, filtroEstatusCalendario]);

  const celdasCalendario = useMemo(
    () => construirCalendario(ejercicio, periodo - 1, filasCalendario),
    [ejercicio, periodo, filasCalendario]
  );

  const celdasSemana = useMemo(
    () => construirSemana(semanaAncla, filasCalendario),
    [semanaAncla, filasCalendario]
  );

  const metricas = useMemo(() => {
    // "Visitas programadas" es el conteo de lo que está actualmente en pantalla (filas, ya
    // filtradas por cliente); "Visitas realizadas" son las de esas mismas filas con estatus R
    const realizadas = filas.filter((v) => v.estatus === "R").length;
    // Sin objetivo (0) pero con al menos una visita realizada, la meta ya se considera cumplida
    const avance = objetivo > 0
      ? Math.min(100, Math.round((realizadas / objetivo) * 100))
      : (realizadas > 0 ? 100 : 0);
    return [
      { label: "Objetivo de visitas", valor: objetivo, color: "#c9a227" },
      { label: "Visitas programadas", valor: filas.length, color: "#8a6d00" },
      { label: "Visitas realizadas", valor: realizadas, color: "#1e5b34" },
      { label: "Avance", valor: `${avance}%`, color: "#315c6f", progreso: avance },
    ];
  }, [filas, objetivo]);

  const resultadosBusqueda = useMemo(() => {
    const q = textoBusqueda.trim().toLowerCase();
    if (!q) return [];
    return clientes.filter((c) => (c.razon_social || "").toLowerCase().includes(q));
  }, [textoBusqueda, clientes]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setMostrarDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSeleccionarCliente = (cliente) => {
    setClienteFiltro(cliente);
    setTextoBusqueda(cliente.razon_social);
    setMostrarDropdown(false);
  };

  const handleLimpiarBusqueda = () => {
    setTextoBusqueda("");
    setClienteFiltro(null);
    setMostrarDropdown(false);
  };

  // Al cambiar de mes desde el toolbar, la semana ancla se reubica al mismo mes: día de hoy
  // si es el mes actual, o el día 1 si es cualquier otro mes
  const reubicarAnclaSemana = (anioNuevo, periodoNuevo) => {
    const esMesActual = anioNuevo === HOY.getFullYear() && periodoNuevo === HOY.getMonth() + 1;
    setSemanaAncla(esMesActual ? HOY : new Date(anioNuevo, periodoNuevo - 1, 1));
  };

  const handlePrevMes = () => {
    let anioNuevo = ejercicio, periodoNuevo = periodo - 1;
    if (periodoNuevo < 1) { anioNuevo -= 1; periodoNuevo = 12; }
    setEjercicio(anioNuevo);
    setPeriodo(periodoNuevo);
    reubicarAnclaSemana(anioNuevo, periodoNuevo);
  };
  const handleNextMes = () => {
    let anioNuevo = ejercicio, periodoNuevo = periodo + 1;
    if (periodoNuevo > 12) { anioNuevo += 1; periodoNuevo = 1; }
    setEjercicio(anioNuevo);
    setPeriodo(periodoNuevo);
    reubicarAnclaSemana(anioNuevo, periodoNuevo);
  };

  // Navegación semanal — mueve la ancla ±7 días; si cruza a otro mes, sincroniza
  // ejercicio/periodo para que el backend cargue las visitas de ese mes
  const handlePrevSemana = () => {
    const nueva = new Date(semanaAncla);
    nueva.setDate(nueva.getDate() - 7);
    setSemanaAncla(nueva);
    if (nueva.getFullYear() !== ejercicio || nueva.getMonth() + 1 !== periodo) {
      setEjercicio(nueva.getFullYear());
      setPeriodo(nueva.getMonth() + 1);
    }
  };
  const handleNextSemana = () => {
    const nueva = new Date(semanaAncla);
    nueva.setDate(nueva.getDate() + 7);
    setSemanaAncla(nueva);
    if (nueva.getFullYear() !== ejercicio || nueva.getMonth() + 1 !== periodo) {
      setEjercicio(nueva.getFullYear());
      setPeriodo(nueva.getMonth() + 1);
    }
  };

  // Regresa a CRM_Dashboard llevando el idcliente actual (mismo patrón que
  // PlantillaScreenRemastered/CRM_DetalleScreen) para que el cliente buscado no se pierda
  const handleRegresar = () => {
    history.push("/DashClientes/Main", clienteFiltro ? { idcliente: clienteFiltro.idcliente } : {});
  };

  const handleAbrirNueva = () => {
    setVisitaEditar(null);
    setModalNuevaAbierto(true);
  };

  const handleAbrirEditar = (visita) => {
    // Una visita ya realizada no se puede editar
    if (visita.estatus === "R") return;
    setVisitaEditar(visita);
    setIdVisitaDetalle(null);
    setVisitaDetalle(null);
    setModalNuevaAbierto(true);
  };

  const handleCerrarNueva = (huboCambios) => {
    setModalNuevaAbierto(false);
    setVisitaEditar(null);
    if (huboCambios) cargarListado();
  };

  const handleAbrirDetalle = (visita) => {
    setIdVisitaDetalle(visita.id_visita);
  };

  useEffect(() => {
    if (!idVisitaDetalle) {
      setVisitaDetalle(null);
      setTimelineDetalle([]);
      return;
    }
    setLoader((prev) => ({ ...prev, mostrar: true }));
    Promise.all([
      request.get(`VisitasCRM/ObtenerVisitaID?idvisita=${idVisitaDetalle}`),
      request.get(`VisitasCRM/ObtenerTimeLine?idvisita=${idVisitaDetalle}`),
    ])
      .then(([visita, timeline]) => {
        setVisitaDetalle(visita);
        setTimelineDetalle(timeline || []);
        setLoader((prev) => ({ ...prev, mostrar: false }));
      })
      .catch((error) => {
        setLoader((prev) => ({ ...prev, mostrar: false }));
        Swal.fire("VISITAS", JSON.stringify(error), "error");
      });
  }, [idVisitaDetalle]);

  const handleCerrarDetalle = () => setIdVisitaDetalle(null);

  const handleAbrirEstatus = (visita) => {
    setIdVisitaDetalle(null);
    setVisitaEstatus(visita);
  };

  const handleCerrarEstatus = (huboCambios) => {
    setVisitaEstatus(null);
    if (huboCambios) cargarListado();
  };

  const handleAbrirComentario = (visita) => {
    setIdVisitaDetalle(null);
    setVisitaComentario(visita);
  };

  const handleCerrarComentario = (huboCambios) => {
    setVisitaComentario(null);
    if (huboCambios) cargarListado();
  };

  const handleAbrirRevertir = (visita) => {
    setIdVisitaDetalle(null);
    setVisitaRevertir(visita);
  };

  const handleCerrarRevertir = (huboCambios) => {
    setVisitaRevertir(null);
    if (huboCambios) cargarListado();
  };

  const handleEliminar = (visita) => {
    Swal.fire({
      title: "¿Eliminar visita?",
      text: `Se eliminará la visita programada a ${visita.razon_social}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#b3261e",
      cancelButtonColor: "#888",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;
      setLoader((prev) => ({ ...prev, mostrar: true }));
      request
        .get(`VisitasCRM/EliminaVisita?id_visita=${visita.id_visita}`)
        .then(async () => {
          setLoader((prev) => ({ ...prev, mostrar: false }));
          await Swal.fire("VISITAS", "Visita eliminada con éxito", "success");
          cargarListado();
        })
        .catch((error) => {
          setLoader((prev) => ({ ...prev, mostrar: false }));
          Swal.fire("VISITAS", JSON.stringify(error), "error");
        });
    });
  };

  const onGeneraExcel = () => {
    let adr = GetKey(boxstate.pv.adr, ["idadr"]);
    let sucursal = GetKey(boxstate.pv.sucursal, ["idsucursal"]);
    if (!adr) adr = "0";
    if (!sucursal) sucursal = "0";

    setLoader((prev) => ({ ...prev, mostrar: true }));
    request
      .get(`ReportesCRM/ImprimirExcelReporteCoberturaVisitas?fechainicio=${boxstate.fechaInicio}&fechafin=${boxstate.fechaFin}&adr=${adr}&sucursal=${sucursal}`)
      .then(({ documento, filename }) => {
        const linkSource = `data:application/vnd.ms-excel;base64,${documento}`;
        const downloadLink = document.createElement("a");
        downloadLink.href = linkSource;
        downloadLink.download = `${filename}.xlsx`;
        downloadLink.click();
        setLoader((prev) => ({ ...prev, mostrar: false }));
      })
      .catch((error) => {
        setLoader((prev) => ({ ...prev, mostrar: false }));
        Swal.fire("COBERTURA DE VISITAS", JSON.stringify(error), "error");
      });
  };

  return (
    <div className="crm-main-screen">
      <LoaderScreen loader={loader} />
      <div className="crm-body">
        <div className="crm-page">
          <div className="crm-module">
            <div className="crm-module-head">
              <div className="crm-head-grid">
                <div className="crm-head-left">
                  <button className="crm-btn-regresar" onClick={handleRegresar}>
                    <ArrowBackIcon /> Regresar
                  </button>
                  <FiltroVisitasProgramadas
                    onCargar={cargarListado}
                    boxState={boxstate}
                    onBoxChange={setBoxState}
                    permisos={permisos}
                  />
                </div>
                <div className="crm-head-center">
                  <p className="crm-module-subtitle">Clientes</p>
                  <h1 className="crm-module-title">PROGRAMAR VISITAS</h1>
                </div>
                <div className="crm-head-right">
                  <button className="crmcs-btn-excel" onClick={onGeneraExcel}>
                    <GetAppIcon /> Excel
                  </button>
                </div>
              </div>
              <div className="crm-head-divider">
                <div className="crm-head-divider-dot" />
              </div>
            </div>

            <div className="crm-home-wrapper vp-wrapper">
              <div className="vp-container">
                <VisitasMetricas metricas={metricas} />

                {vista === "calendario" && (
                  idclienteFijo ? (
                    <div className="vp-search-row">
                      <div className="vp-search-bar vp-search-bar-fijo">
                        <AccountCircleOutlinedIcon className="vp-search-icon" />
                        <span className="vp-search-fijo-nombre">{clienteFiltro?.razon_social || nombreClienteFijo}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="vp-search-row" ref={searchRef}>
                      <div className={`vp-search-bar${mostrarDropdown && resultadosBusqueda.length > 0 ? " vp-search-bar-open" : ""}`}>
                        <SearchIcon className="vp-search-icon" />
                        <input
                          className="vp-search-input"
                          type="text"
                          placeholder="Buscar cliente..."
                          value={textoBusqueda}
                          onChange={(e) => {
                            setTextoBusqueda(e.target.value);
                            setMostrarDropdown(true);
                            if (clienteFiltro) setClienteFiltro(null);
                          }}
                          onFocus={() => textoBusqueda && setMostrarDropdown(true)}
                          autoComplete="off"
                        />
                        {textoBusqueda && (
                          <button type="button" className="vp-search-clear" onClick={handleLimpiarBusqueda}>
                            <ClearIcon />
                          </button>
                        )}
                      </div>

                      {mostrarDropdown && textoBusqueda && (
                        <div className="vp-search-dropdown">
                          {resultadosBusqueda.length > 0 ? (
                            resultadosBusqueda.map((c) => (
                              <div key={c.idcliente} className="vp-search-result" onClick={() => handleSeleccionarCliente(c)}>
                                <div className="vp-search-result-avatar"><AccountCircleOutlinedIcon /></div>
                                <div className="vp-search-result-info">
                                  <span className="vp-search-result-nombre">{c.razon_social}</span>
                                  <span className="vp-search-result-meta">
                                    <BadgeIcon /> {c.rfc}
                                  </span>
                                </div>
                                <ArrowForwardIosIcon className="vp-search-result-arrow" />
                              </div>
                            ))
                          ) : (
                            <div className="vp-search-no-result">No se encontraron clientes con "{textoBusqueda}"</div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                )}

                <div className="vp-toolbar">
                  <div className="vp-mes-nav">
                    <button className="vp-nav-btn" onClick={handlePrevMes}>&lsaquo;</button>
                    <h2 className="vp-mes-label">
                      {MESES[periodo - 1]} <span>{ejercicio}</span>
                    </h2>
                    <button className="vp-nav-btn" onClick={handleNextMes}>&rsaquo;</button>
                  </div>

                  <div className="vp-toolbar-right">
                    <div className="vp-toggle">
                      <button className={vista === "listado" ? "activo" : ""} onClick={() => setVista("listado")}>
                        Listado
                      </button>
                      <button className={vista === "calendario" ? "activo" : ""} onClick={() => setVista("calendario")}>
                        Calendario
                      </button>
                    </div>

                    {vista === "calendario" && (
                      <div className="vp-toggle">
                        <button
                          className={modoCalendario === "mes" ? "activo" : ""}
                          onClick={() => setModoCalendario("mes")}
                        >
                          Mes
                        </button>
                        <button
                          className={modoCalendario === "semana" ? "activo" : ""}
                          onClick={() => setModoCalendario("semana")}
                        >
                          Semana
                        </button>
                      </div>
                    )}

                    {vista === "calendario" && (
                      <select
                        className="vp-select-estatus"
                        value={filtroEstatusCalendario}
                        onChange={(e) => setFiltroEstatusCalendario(e.target.value)}
                      >
                        <option value="">Todos los estatus</option>
                        {ESTATUS_VISITA.map((opt) => (
                          <option key={opt.codigo} value={opt.codigo}>{opt.texto}</option>
                        ))}
                      </select>
                    )}

                    <button className="vp-btn-nueva" onClick={handleAbrirNueva}>
                      <AddIcon /> Nueva visita
                    </button>
                  </div>
                </div>

                {vista === "calendario" && clienteFiltro && !idclienteFijo && (
                  <div className="vp-filtro-activo">
                    Filtrando por: <strong>{clienteFiltro.razon_social}</strong>
                    <button type="button" onClick={handleLimpiarBusqueda}>
                      <ClearIcon fontSize="small" /> Quitar filtro
                    </button>
                  </div>
                )}

                {vista === "calendario" && modoCalendario === "semana" && (
                  <div className="vp-toolbar vp-toolbar-calendario">
                    <div className="vp-mes-nav">
                      <button className="vp-nav-btn" onClick={handlePrevSemana}>&lsaquo;</button>
                      <span className="vp-semana-label">
                        {celdasSemana[0]?.numero} {MESES[celdasSemana[0]?.mes]?.slice(0, 3)} - {celdasSemana[6]?.numero} {MESES[celdasSemana[6]?.mes]?.slice(0, 3)}
                      </span>
                      <button className="vp-nav-btn" onClick={handleNextSemana}>&rsaquo;</button>
                    </div>
                  </div>
                )}

                <div className="vp-contenido scroll">
                  {vista === "listado" ? (
                    <VisitasListado
                      filas={filas}
                      onOpenDetalle={handleAbrirDetalle}
                      onOpenEstatus={setVisitaEstatus}
                      onEliminar={handleEliminar}
                      ocultarAsesor={esRolVesc}
                      puedeRevertir={esRolUrvr}
                      onRevertirEstatus={handleAbrirRevertir}
                    />
                  ) : (
                    <VisitasCalendario
                      modo={modoCalendario}
                      celdas={celdasCalendario}
                      semana={celdasSemana}
                      hoy={HOY.getDate()}
                      hoyMes={HOY.getMonth()}
                      hoyAnio={HOY.getFullYear()}
                      mesActual={periodo - 1}
                      anioActual={ejercicio}
                      onOpenDetalle={handleAbrirDetalle}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <VisitasModalNueva
        open={modalNuevaAbierto}
        onClose={handleCerrarNueva}
        visita={visitaEditar}
        clientes={clientes}
        opcionesTipoVisita={opcionesTipoVisita}
        opcionesLineas={opcionesLineas}
        idvendedor={usuario}
        clienteFijo={idclienteFijo ? clienteFiltro : null}
      />
      <VisitasModalDetalle
        open={!!idVisitaDetalle}
        visita={visitaDetalle}
        timeline={timelineDetalle}
        onClose={handleCerrarDetalle}
        onEditar={handleAbrirEditar}
        onEditarEstatus={handleAbrirEstatus}
        onAgregarComentario={handleAbrirComentario}
        puedeRevertir={esRolUrvr}
        onRevertirEstatus={handleAbrirRevertir}
      />
      <VisitasModalEstatus
        open={!!visitaEstatus}
        visita={visitaEstatus}
        onClose={handleCerrarEstatus}
      />
      <VisitasModalComentario
        open={!!visitaComentario}
        visita={visitaComentario}
        onClose={handleCerrarComentario}
      />
      <VisitasModalRevertir
        open={!!visitaRevertir}
        visita={visitaRevertir}
        onClose={handleCerrarRevertir}
      />
    </div>
  );
};
